import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { lightThemeColors } from "../../assets/styles/colors";
import PrimaryButton from "../../Components/Generic/PrimaryButton";
import { ReactComponent as BinIcon } from "../../assets/icons/bin-emotii.svg";
import { useProjectContext } from "../../context/useProjectContext";
import { useOutletContext } from "react-router-dom";
import { showGenericError, showSuccessNotification, showErrorNotification } from "../../helper/Notifications";
import CustomModal from "../../Components/Generic/CustomModal";
import CustomInput from "../../Components/Generic/CustomInput";
import Tooltip from "../../Components/Generic/Tooltip";
import SecondaryButton from "../../Components/Generic/SecondaryButton";
import { executeBulkDelete } from "../../helper/bulkOperations";
import BulkActionBar from "../../Components/Generic/BulkActionBar";
import StyledTable, {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../Components/Generic/StyledTable";
import TablePagination from "../../Components/Generic/TablePagination";
import { useLicensingContext } from "../../context/useLicensingContext";
import { ReactComponent as PlusIcon } from "../../assets/icons/plusIcon.svg";
import LoaderComponent from "../../Components/Generic/LoaderComponent";
import { getTranslationLanguagesAPICall } from "../../api/keys/keyService";
import {
  getGlossaryTermsAPICall,
  createGlossaryTermAPICall,
  updateGlossaryTermAPICall,
  deleteGlossaryTermAPICall,
} from "../../api/glossary/glossaryService";
import PageHeader from "../../Components/Layout/PageHeader";

const BEHAVIORS = [
  { value: "preserve", label: "Preserve" },
  { value: "expand", label: "Expand" },
  { value: "custom", label: "Custom" },
];

const GlossaryPage = () => {
  const [terms, setTerms] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [termToDelete, setTermToDelete] = useState(null);
  const [termToEdit, setTermToEdit] = useState(null);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Form state for add modal
  const [newTerm, setNewTerm] = useState("");
  const [newBehavior, setNewBehavior] = useState("preserve");
  const [newDefinition, setNewDefinition] = useState("");
  const [selectedTerms, setSelectedTerms] = useState([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [newInstruction, setNewInstruction] = useState("");
  const [newSourceLanguage, setNewSourceLanguage] = useState("");
  const [newTargetLanguage, setNewTargetLanguage] = useState("");
  const [newProductSlugs, setNewProductSlugs] = useState([]);

  // Form state for edit modal
  const [editTerm, setEditTerm] = useState("");
  const [editBehavior, setEditBehavior] = useState("preserve");
  const [editDefinition, setEditDefinition] = useState("");
  const [editInstruction, setEditInstruction] = useState("");
  const [editSourceLanguage, setEditSourceLanguage] = useState("");
  const [editTargetLanguage, setEditTargetLanguage] = useState("");
  const [editProductSlugs, setEditProductSlugs] = useState([]);

  const { selectedProject } = useProjectContext();
  const { subscription } = useLicensingContext();
  const { setLoading, setShowSupportModal } = useOutletContext();

  const isEnterprise = true;
  const subscribedProducts = (subscription?.items || [])
    .filter((item) => item.product_slug && item.product_name)
    .map((item) => ({ slug: item.product_slug, name: item.product_name }))
    .filter((p, i, arr) => arr.findIndex((x) => x.slug === p.slug) === i);

  const handleGetTerms = () => {
    if (!selectedProject?.id) return;

    getGlossaryTermsAPICall(
      selectedProject.id,
      page,
      itemsPerPage,
      searchTerm,
      (response) => {
        const termsData = response.data.glossary_terms || response.data || [];
        // Sort terms alphabetically
        const sortedTerms = [...termsData].sort((a, b) =>
          a.term.toLowerCase().localeCompare(b.term.toLowerCase())
        );
        setTerms(sortedTerms);
        setTotalPages(response.data.pagination?.total_pages || 1);
        setLoading(false);
      },
      () => {
        showGenericError();
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    if (selectedProject) {
      setPage(1);
    }
  }, [selectedProject, searchTerm]);

  useEffect(() => {
    if (selectedProject) {
      handleGetTerms();
    }
  }, [selectedProject, page, itemsPerPage, searchTerm]);

  // Fetch available translation languages
  useEffect(() => {
    getTranslationLanguagesAPICall(
      (response) => {
        setAvailableLanguages(response.data.languages || []);
      },
      (error) => {
        console.error("Failed to fetch languages:", error);
      }
    );
  }, []);

  const handleOpenDeleteModal = (term) => {
    setTermToDelete(term);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setTermToDelete(null);
  };

  const handleDeleteTerm = () => {
    return new Promise((resolve, reject) => {
      setLoading(true);
      deleteGlossaryTermAPICall(
        selectedProject.id,
        termToDelete.id,
        (response) => {
          handleGetTerms();
          resolve();
        },
        (error) => {
          const errors = error?.response?.data?.errors;
          const errorMessage = error?.response?.data?.message;
          if (errors?.length > 0) {
            showErrorNotification(errors[0]);
          } else if (errorMessage) {
            showErrorNotification(errorMessage);
          } else {
            showGenericError();
          }
          setLoading(false);
          reject(error);
        }
      );
    });
  };
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = terms.map((term) => term.id);
      setSelectedTerms(allIds);
    } else {
      setSelectedTerms([]);
    }
  };

  const handleSelectOne = (e, termId) => {
    if (e.target.checked) {
      setSelectedTerms((prev) => [...prev, termId]);
    } else {
      setSelectedTerms((prev) => prev.filter((id) => id !== termId));
    }
  };

  const handleBulkDeleteTerms = () => {
    const deleteSingleTerm = (termId) => {
      return new Promise((resolve, reject) => {
        deleteGlossaryTermAPICall(selectedProject.id, termId, resolve, reject);
      });
    };

    return executeBulkDelete({
      items: selectedTerms,
      deleteAction: deleteSingleTerm,
      onSuccess: () => {
        handleGetTerms();
        setSelectedTerms([]);
      },
      setLoading: setLoading
    });
  };

  const handleOpenEditModal = (term) => {
    setTermToEdit(term);
    setEditTerm(term.term);
    setEditBehavior(term.behavior || "preserve");
    setEditDefinition(term.definition || "");
    setEditInstruction(term.instruction || "");
    setEditSourceLanguage(term.source_language || "");
    setEditTargetLanguage(term.target_language || "");
    setEditProductSlugs(term.product_slugs || []);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setTermToEdit(null);
    resetEditForm();
  };

  const resetEditForm = () => {
    setEditTerm("");
    setEditBehavior("preserve");
    setEditDefinition("");
    setEditInstruction("");
    setEditSourceLanguage("");
    setEditTargetLanguage("");
    setEditProductSlugs([]);
  };

  const handleCreateTerm = async () => {
    setLoading(true);

    try {
      const data = {
        term: newTerm.trim(),
        behavior: newBehavior,
      };

      if (newBehavior === "expand" && newDefinition.trim()) {
        data.definition = newDefinition.trim();
      }

      if (newBehavior === "custom" && newInstruction.trim()) {
        data.instruction = newInstruction.trim();
      }

      if (newSourceLanguage) {
        data.source_language = newSourceLanguage;
      }

      if (newTargetLanguage) {
        data.target_language = newTargetLanguage;
      }

      if (isEnterprise && newProductSlugs.length > 0) {
        data.product_slugs = newProductSlugs;
      }

      await new Promise((resolve, reject) => {
        createGlossaryTermAPICall(
          selectedProject.id,
          data,
          (response) => resolve(),
          (error) => reject(error)
        );
      });

      handleGetTerms();
      resetAddForm();
      return Promise.resolve();
    } catch (error) {
      const errors = error?.response?.data?.errors;
      const errorMessage = error?.response?.data?.message;
      if (errors?.length > 0) {
        showErrorNotification(errors[0]);
      } else if (errorMessage) {
        showErrorNotification(errorMessage);
      } else {
        showGenericError();
      }
      setLoading(false);
      return Promise.reject(error);
    }
  };

  const handleUpdateTerm = async () => {
    setLoading(true);

    try {
      const data = {
        term: editTerm.trim(),
        behavior: editBehavior,
      };

      if (editBehavior === "expand") {
        data.definition = editDefinition.trim();
      }

      if (editBehavior === "custom") {
        data.instruction = editInstruction.trim();
      }

      if (editSourceLanguage) {
        data.source_language = editSourceLanguage;
      } else {
        data.source_language = null;
      }

      if (editTargetLanguage) {
        data.target_language = editTargetLanguage;
      } else {
        data.target_language = null;
      }

      if (isEnterprise) {
        data.product_slugs = editProductSlugs;
      }

      await new Promise((resolve, reject) => {
        updateGlossaryTermAPICall(
          selectedProject.id,
          termToEdit.id,
          data,
          (response) => resolve(),
          (error) => reject(error)
        );
      });

      handleGetTerms();
      handleCloseEditModal();
      return Promise.resolve();
    } catch (error) {
      const errors = error?.response?.data?.errors;
      const errorMessage = error?.response?.data?.message;
      if (errors?.length > 0) {
        showErrorNotification(errors[0]);
      } else if (errorMessage) {
        showErrorNotification(errorMessage);
      } else {
        showGenericError();
      }
      setLoading(false);
      return Promise.reject(error);
    }
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    resetAddForm();
  };

  const resetAddForm = () => {
    setNewTerm("");
    setNewBehavior("preserve");
    setNewDefinition("");
    setNewInstruction("");
    setNewSourceLanguage("");
    setNewTargetLanguage("");
    setNewProductSlugs([]);
  };

  const isAddFormValid = () => {
    if (!newTerm.trim()) return false;
    if (newBehavior === "expand" && !newDefinition.trim()) return false;
    if (newBehavior === "custom" && !newInstruction.trim()) return false;
    return true;
  };

  const isEditFormValid = () => {
    if (!editTerm.trim()) return false;
    if (editBehavior === "expand" && !editDefinition.trim()) return false;
    if (editBehavior === "custom" && !editInstruction.trim()) return false;
    return true;
  };

  const getBehaviorLabel = (behavior) => {
    const found = BEHAVIORS.find(b => b.value === behavior);
    return found ? found.label : behavior;
  };

  const getBehaviorColor = (behavior) => {
    switch (behavior) {
      case "preserve":
        return { bg: "#EFF8FF", text: "#175CD3" };
      case "expand":
        return { bg: "#F4EBFF", text: "#7F56D9" };
      case "custom":
        return { bg: "#FEF0C7", text: "#DC6803" };
      default:
        return { bg: "#F2F4F7", text: "#344054" };
    }
  };

  const getTermDetails = (term) => {
    if (term.behavior === "expand" && term.definition) {
      return term.definition;
    }
    if (term.behavior === "custom" && term.instruction) {
      return term.instruction;
    }
    return "-";
  };

  const getLanguageDisplay = (term) => {
    const source = term.source_language?.toUpperCase() || "Any";
    const target = term.target_language?.toUpperCase() || "Any";
    return `${source} → ${target}`;
  };

  const getLanguageName = (code) => {
    const lang = availableLanguages.find(l => l.code === code);
    return lang ? lang.name : code;
  };

  const hasSubscription = subscription !== null && subscription !== undefined;
  const hasSelection = selectedTerms.length > 0;

  return (
    <Container>
      {/* Delete Term Modal */}
      <CustomModal
        isOpen={showDeleteModal}
        onOpenChange={handleCloseDeleteModal}
        iconType="warning"
        iconAlignment="center"
        title="Delete this glossary term?"
        titleAlignment="center"
        buttonLayout="row"
        showCloseButton={false}
        autoCloseOnSuccess={true}
        width={400}
        buttons={[
          {
            text: "Cancel",
            onClick: handleCloseDeleteModal,
            variant: "secondary"
          },
          {
            text: "Delete Term",
            action: handleDeleteTerm,
            variant: "delete",
            successMessage: "Glossary term deleted successfully"
          }
        ]}
      >
        <ModalContentText>
          This will permanently remove the term "{termToDelete?.term}" from your glossary. This action cannot be undone.
        </ModalContentText>
      </CustomModal>
      {/* Bulk Delete Modal */}
      <CustomModal
        isOpen={showBulkDeleteModal}
        onOpenChange={() => setShowBulkDeleteModal(false)}
        iconType="warning"
        iconAlignment="center"
        title={`Delete ${selectedTerms.length} glossary term${selectedTerms.length === 1 ? '' : 's'}?`}
        titleAlignment="center"
        buttonLayout="row"
        showCloseButton={false}
        autoCloseOnSuccess={true}
        width={400}
        buttons={[
          {
            text: "Cancel",
            onClick: () => setShowBulkDeleteModal(false),
            variant: "secondary"
          },
          {
            text: `Delete ${selectedTerms.length} Term${selectedTerms.length === 1 ? '' : 's'}`,
            action: handleBulkDeleteTerms,
            variant: "delete",
            successMessage: `Glossary term${selectedTerms.length === 1 ? '' : 's'} deleted successfully`
          }
        ]}
      >
        <ModalContentText>
          This will permanently remove <strong>{selectedTerms.length}</strong> term{selectedTerms.length === 1 ? '' : 's'} from your glossary. You cannot recover {selectedTerms.length === 1 ? 'this term' : 'these terms'} once deleted.
        </ModalContentText>
      </CustomModal>  
      {/* Add Term Modal */}
      <CustomModal
        isOpen={showAddModal}
        onOpenChange={handleCloseAddModal}
        iconType="apiKey"
        iconAlignment="left"
        title="Add Glossary Term"
        subtitle="Define how specific terms should be handled during translation."
        titleAlignment="left"
        buttonLayout="row"
        showCloseButton={false}
        autoCloseOnSuccess={true}
        width={480}
        buttons={[
          {
            text: "Cancel",
            onClick: handleCloseAddModal,
            variant: "secondary"
          },
          {
            text: "Add Term",
            action: handleCreateTerm,
            variant: "primary",
            disabled: !isAddFormValid(),
            successMessage: "Glossary term created successfully"
          }
        ]}
      >
        <ModalContent>
          <CustomInput
            value={newTerm}
            onChange={(e) => setNewTerm(e.currentTarget.value)}
            label="Term"
            placeholder="Enter word or phrase to match"
          />

          <FormGroup>
            <FormLabel>Behavior</FormLabel>
            <SelectWrapper>
              <StyledSelect
                value={newBehavior}
                onChange={(e) => setNewBehavior(e.target.value)}
              >
                {BEHAVIORS.map((b) => (
                  <option key={b.value} value={b.value}>
                    {b.label}
                  </option>
                ))}
              </StyledSelect>
            </SelectWrapper>
            <BehaviorHint>
              {newBehavior === "preserve" && "Keep exactly as written, don't translate (for brand names, product names)"}
              {newBehavior === "expand" && "Provide a definition so the LLM understands the term (for acronyms, internal jargon)"}
              {newBehavior === "custom" && "Write your own instruction for how to handle this term"}
            </BehaviorHint>
          </FormGroup>

          {newBehavior === "expand" && (
            <FormGroup>
              <FormLabel>Definition</FormLabel>
              <StyledTextarea
                value={newDefinition}
                onChange={(e) => setNewDefinition(e.target.value)}
                placeholder="e.g., Software Development Kit"
                rows={2}
              />
            </FormGroup>
          )}

          {newBehavior === "custom" && (
            <FormGroup>
              <FormLabel>Instruction</FormLabel>
              <StyledTextarea
                value={newInstruction}
                onChange={(e) => setNewInstruction(e.target.value)}
                placeholder="e.g., Keep as 'emotii' - this is our brand name, never translate"
                rows={3}
              />
            </FormGroup>
          )}

          <LanguageRow>
            <FormGroup style={{ flex: 1 }}>
              <FormLabel>Source Language (optional)</FormLabel>
              <SelectWrapper>
                <StyledSelect
                  value={newSourceLanguage}
                  onChange={(e) => setNewSourceLanguage(e.target.value)}
                >
                  <option value="">Any language</option>
                  {availableLanguages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </StyledSelect>
              </SelectWrapper>
            </FormGroup>
            <FormGroup style={{ flex: 1 }}>
              <FormLabel>Target Language (optional)</FormLabel>
              <SelectWrapper>
                <StyledSelect
                  value={newTargetLanguage}
                  onChange={(e) => setNewTargetLanguage(e.target.value)}
                >
                  <option value="">Any language</option>
                  {availableLanguages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </StyledSelect>
              </SelectWrapper>
            </FormGroup>
          </LanguageRow>

          {isEnterprise && subscribedProducts.length > 0 && (
            <FormGroup>
              <FormLabel>Apply to Products (optional)</FormLabel>
              <ProductPillsRow>
                {subscribedProducts.map((product) => {
                  const selected = newProductSlugs.includes(product.slug);
                  return (
                    <ProductPill
                      key={product.slug}
                      $selected={selected}
                      onClick={() =>
                        setNewProductSlugs((prev) =>
                          selected
                            ? prev.filter((s) => s !== product.slug)
                            : [...prev, product.slug]
                        )
                      }
                    >
                      {product.name}
                    </ProductPill>
                  );
                })}
              </ProductPillsRow>
              <BehaviorHint>
                {newProductSlugs.length === 0
                  ? "No products selected — this term applies globally across all products"
                  : "This term will only apply when translating within the selected products"}
              </BehaviorHint>
            </FormGroup>
          )}
        </ModalContent>
      </CustomModal>

      {/* Edit Term Modal */}
      <CustomModal
        isOpen={showEditModal}
        onOpenChange={handleCloseEditModal}
        iconType="apiKey"
        iconAlignment="left"
        title="Edit Glossary Term"
        subtitle="Update how this term should be handled during translation."
        titleAlignment="left"
        buttonLayout="row"
        showCloseButton={false}
        autoCloseOnSuccess={true}
        width={480}
        buttons={[
          {
            text: "Cancel",
            onClick: handleCloseEditModal,
            variant: "secondary"
          },
          {
            text: "Save Changes",
            action: handleUpdateTerm,
            variant: "primary",
            disabled: !isEditFormValid(),
            successMessage: "Glossary term updated successfully"
          }
        ]}
      >
        <ModalContent>
          <CustomInput
            value={editTerm}
            onChange={(e) => setEditTerm(e.currentTarget.value)}
            label="Term"
            placeholder="Enter word or phrase to match"
          />

          <FormGroup>
            <FormLabel>Behavior</FormLabel>
            <SelectWrapper>
              <StyledSelect
                value={editBehavior}
                onChange={(e) => setEditBehavior(e.target.value)}
              >
                {BEHAVIORS.map((b) => (
                  <option key={b.value} value={b.value}>
                    {b.label}
                  </option>
                ))}
              </StyledSelect>
            </SelectWrapper>
            <BehaviorHint>
              {editBehavior === "preserve" && "Keep exactly as written, don't translate (for brand names, product names)"}
              {editBehavior === "expand" && "Provide a definition so the LLM understands the term (for acronyms, internal jargon)"}
              {editBehavior === "custom" && "Write your own instruction for how to handle this term"}
            </BehaviorHint>
          </FormGroup>

          {editBehavior === "expand" && (
            <FormGroup>
              <FormLabel>Definition</FormLabel>
              <StyledTextarea
                value={editDefinition}
                onChange={(e) => setEditDefinition(e.target.value)}
                placeholder="e.g., Software Development Kit"
                rows={2}
              />
            </FormGroup>
          )}

          {editBehavior === "custom" && (
            <FormGroup>
              <FormLabel>Instruction</FormLabel>
              <StyledTextarea
                value={editInstruction}
                onChange={(e) => setEditInstruction(e.target.value)}
                placeholder="e.g., Keep as 'emotii' - this is our brand name, never translate"
                rows={3}
              />
            </FormGroup>
          )}

          <LanguageRow>
            <FormGroup style={{ flex: 1 }}>
              <FormLabel>Source Language (optional)</FormLabel>
              <SelectWrapper>
                <StyledSelect
                  value={editSourceLanguage}
                  onChange={(e) => setEditSourceLanguage(e.target.value)}
                >
                  <option value="">Any language</option>
                  {availableLanguages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </StyledSelect>
              </SelectWrapper>
            </FormGroup>
            <FormGroup style={{ flex: 1 }}>
              <FormLabel>Target Language (optional)</FormLabel>
              <SelectWrapper>
                <StyledSelect
                  value={editTargetLanguage}
                  onChange={(e) => setEditTargetLanguage(e.target.value)}
                >
                  <option value="">Any language</option>
                  {availableLanguages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </StyledSelect>
              </SelectWrapper>
            </FormGroup>
          </LanguageRow>

          {isEnterprise && subscribedProducts.length > 0 && (
            <FormGroup>
              <FormLabel>Apply to Products (optional)</FormLabel>
              <ProductPillsRow>
                {subscribedProducts.map((product) => {
                  const selected = editProductSlugs.includes(product.slug);
                  return (
                    <ProductPill
                      key={product.slug}
                      $selected={selected}
                      onClick={() =>
                        setEditProductSlugs((prev) =>
                          selected
                            ? prev.filter((s) => s !== product.slug)
                            : [...prev, product.slug]
                        )
                      }
                    >
                      {product.name}
                    </ProductPill>
                  );
                })}
              </ProductPillsRow>
              <BehaviorHint>
                {editProductSlugs.length === 0
                  ? "No products selected — this term applies globally across all products"
                  : "This term will only apply when translating within the selected products"}
              </BehaviorHint>
            </FormGroup>
          )}
        </ModalContent>
      </CustomModal>

      <PageContent>
        <Header>
          <HeaderContent>
            <PageHeader
              title="Glossary"
              subtitle="Manage rules for terms, special expressions and their translations"
            />
          </HeaderContent>
          {hasSubscription && (
            <ButtonsContainer>
              
              <PrimaryButton
                icon={<PlusIcon />}
                text={"Add Term"}
                onClick={() => setShowAddModal(true)}
              />
            </ButtonsContainer>
          )}
        </Header>

        {!hasSubscription ? (
          <RestrictedAccessCard>
            <RestrictedContent>
              <RestrictedTitle>Glossary requires a subscription</RestrictedTitle>
              <RestrictedDescription>
                The glossary feature allows you to define how specific terms, brand names, and expressions should be handled during translation. Upgrade to access this feature.
              </RestrictedDescription>
              <ButtonWrapper>
                <PrimaryButton
                  text="Talk with our Team"
                  onClick={() => setShowSupportModal(true)}
                />
              </ButtonWrapper>
            </RestrictedContent>
          </RestrictedAccessCard>
        ) : (
          <>
            <SearchContainer>
              <CustomInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.currentTarget.value)}
                placeholder="Search terms..."
                wrapperStyle={{ maxWidth: "400px" }}
                className="h-9 py-1.5"
              />
            </SearchContainer>
            <StyledTable>
            <TableHeader>
              
            <BulkActionBar 
                selectedCount={selectedTerms.length} 
                onDelete={() => setShowBulkDeleteModal(true)} 
                onClear={() => setSelectedTerms([])} 
              />
              

              <TableRow>
                <TableHead style={{ width: "48px", verticalAlign: "middle", padding: "0 8px" }}>
                  <CheckboxContainer $alwaysVisible={hasSelection}>
                    <input
                      type="checkbox"
                      style={{ cursor: "pointer", margin: 0 }}
                      checked={
                        terms?.length > 0 &&
                        selectedTerms.length === terms.length
                      }
                      onChange={handleSelectAll}
                    />
                  </CheckboxContainer>
                </TableHead>
                  <TableHead style={{ textAlign: "left", width: "18%" }}>Term</TableHead>
                <TableHead style={{ textAlign: "center", width: "120px" }}>Behavior</TableHead>
                <TableHead style={{ textAlign: "left" }}>Details</TableHead>
                <TableHead style={{ textAlign: "center", width: "140px" }}>Languages</TableHead>
                <TableHead style={{ textAlign: "center", width: "180px" }}>Products</TableHead>
                <TableHead style={{ width: "100px", textAlign: "center" }}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {terms && terms.length > 0 ? (
                terms.map((term) => (
                  <TableRow key={term.id}>
                    <TableCell style={{ width: "48px", verticalAlign: "middle", padding: "0 8px" }}>
                      <CheckboxContainer $alwaysVisible={hasSelection || selectedTerms.includes(term.id)}>
                        <input
                          type="checkbox"
                          style={{ cursor: "pointer", margin: 0 }}
                          checked={selectedTerms.includes(term.id)}
                          onChange={(e) => handleSelectOne(e, term.id)}
                        />
                      </CheckboxContainer>
                    </TableCell>
                    <TableCell>
                      <TermNameContainer>
                        <TermIconWrapper>
                          <TermIcon />
                        </TermIconWrapper>
                        <TermName>{term.term}</TermName>
                      </TermNameContainer>
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      <BehaviorBadge $colors={getBehaviorColor(term.behavior)}>
                        {getBehaviorLabel(term.behavior)}
                      </BehaviorBadge>
                    </TableCell>
                    <TableCell style={{ textAlign: "left" }}>
                      <DetailsText>{getTermDetails(term)}</DetailsText>
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      <LanguageText>{getLanguageDisplay(term)}</LanguageText>
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      {term.product_slugs && term.product_slugs.length > 0 ? (
                        <ProductPillsRow style={{ flexWrap: "wrap", gap: "4px" }}>
                          {term.product_slugs.map((slug) => {
                            const product = subscribedProducts.find((p) => p.slug === slug);
                            return (
                              <ProductPill key={slug} $selected={false} $compact>
                                {product ? product.name : slug}
                              </ProductPill>
                            );
                          })}
                        </ProductPillsRow>
                      ) : (
                        <LanguageText>All</LanguageText>
                      )}
                    </TableCell>
                    <TableCell style={{ width: "100px" }}>
                      <ActionButtonsContainer>
                        <Tooltip content="Edit term" side="top">
                          <EditButton onClick={() => handleOpenEditModal(term)}>
                            <PenIcon />
                          </EditButton>
                        </Tooltip>
                        <Tooltip content="Delete term" side="top">
                          <DeleteButton onClick={() => handleOpenDeleteModal(term)}>
                            <BinIcon />
                          </DeleteButton>
                        </Tooltip>
                      </ActionButtonsContainer>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7}>
                    <NoTermsText>
                      {searchTerm
                        ? `No terms matching "${searchTerm}"`
                        : "No glossary terms yet. Add terms to control how specific words and phrases are handled during translation."}
                    </NoTermsText>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            <TablePagination
              page={page}
              totalPages={totalPages}
              onPrevious={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => p + 1)}
              leftColSpan={2}
              centerColSpan={3}
              rightColSpan={2}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={(v) => { setItemsPerPage(v); setPage(1); }}
            />
            </StyledTable>
          </>
        )}
      </PageContent>
    </Container>
  );
};

export default GlossaryPage;

// Icon for term rows
const TermIcon = (props) => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <rect width="40" height="40" rx="20" fill="#F4EBFF"/>
    <path d="M13 14H27M13 20H27M13 26H21" stroke="#7F56D9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PenIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.419 1.44775 12.6666 1.44775C12.9143 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4087 2.61182C14.5035 2.84063 14.5523 3.08569 14.5523 3.33337C14.5523 3.58106 14.5035 3.82612 14.4087 4.05493C14.314 4.28375 14.1751 4.49162 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  overflow-y: auto;
  padding: 40px 30px 30px 30px;
  position: relative;
  height: 100%;
  background: #F9FAFB;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  background: white;
  padding: 0;
  margin: -40px -30px 0 -30px;
  padding: 30px 30px 10px 30px;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderTitle = styled.h1`
  color: ${lightThemeColors.darkColor};
  font-size: 32px;
  font-weight: 700;
  line-height: 40px;
  text-align: left;
  margin: 0;
`;

const HeaderSubtitle = styled.p`
  color: #667085;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  margin: 0;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const ModalContentText = styled.p`
  color: #667085;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  margin: 0;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FormLabel = styled.label`
  color: ${lightThemeColors.darkColor};
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
`;

const SelectWrapper = styled.div`
  position: relative;
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #D5D7DA;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 400;
  color: ${lightThemeColors.darkColor};
  background: white;
  cursor: pointer;
  outline: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23667085' stroke-width='1.67' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 40px;

  &:focus {
    border-color: #7F56D9;
    box-shadow: 0 0 0 4px rgba(127, 86, 217, 0.1);
  }
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #D5D7DA;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 400;
  color: ${lightThemeColors.darkColor};
  background: white;
  resize: vertical;
  min-height: 60px;
  outline: none;
  font-family: inherit;

  &:focus {
    border-color: #7F56D9;
    box-shadow: 0 0 0 4px rgba(127, 86, 217, 0.1);
  }

  &::placeholder {
    color: #9CA3AF;
  }
`;

const BehaviorHint = styled.p`
  color: #667085;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  margin: 0;
`;

const LanguageRow = styled.div`
  display: flex;
  gap: 16px;
`;

const TermNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TermIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 40px;
    height: 40px;
    display: block;
  }
`;

const TermName = styled.span`
  color: ${lightThemeColors.darkColor};
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
`;

const BehaviorBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.$colors?.bg || '#F2F4F7'};
  color: ${props => props.$colors?.text || '#344054'};
`;

const DetailsText = styled.span`
  color: #667085;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LanguageText = styled.span`
  color: #667085;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  transition: all 0.2s;

  &:hover {
    background: #f4ebff;
    color: #7f56d9;
  }

  svg {
    width: 16px;
    height: 16px;
    fill: none !important;
    stroke: currentColor !important;
    stroke-width: 1.5 !important;
  }

  svg path {
    fill: none !important;
    stroke: currentColor !important;
    stroke-width: 1.5 !important;
  }
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  transition: all 0.2s;

  &:hover {
    background: #fef2f2;
    color: #ef4444;
  }

  svg {
    width: 16px;
    height: 16px;
    fill: none !important;
    stroke: currentColor !important;
    stroke-width: 1.5 !important;
  }

  svg path {
    fill: none !important;
    stroke: currentColor !important;
    stroke-width: 1.5 !important;
  }
`;

const NoTermsText = styled.div`
  color: #667085;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  width: 100%;
  padding: 40px 20px;
`;

const CheckboxContainer = styled.div`
  opacity: ${(props) => (props.$alwaysVisible ? 1 : 0)};
  transition: opacity 0.15s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;

  tr:hover & {
    opacity: 1;
  }
`;
const RestrictedAccessCard = styled.div`
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  padding: 48px;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
`;

const RestrictedContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  text-align: center;
`;

const RestrictedTitle = styled.h2`
  color: #7F56D9;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  margin: 0;
`;

const RestrictedDescription = styled.p`
  color: ${lightThemeColors.darkColor};
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  margin: 0;
`;

const ButtonWrapper = styled.div`
  width: 100%;
  margin-top: 8px;

  button {
    width: 100% !important;
  }
`;

const ProductPillsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
`;

const ProductPill = styled.button`
  padding: ${(props) => (props.$compact ? "2px 8px" : "6px 12px")};
  border-radius: 16px;
  font-size: ${(props) => (props.$compact ? "12px" : "13px")};
  font-weight: 500;
  cursor: ${(props) => (props.$compact ? "default" : "pointer")};
  border: 1.5px solid ${(props) => (props.$selected ? "#7F56D9" : "#D5D7DA")};
  background: ${(props) => (props.$selected ? "#F4EBFF" : "#F9FAFB")};
  color: ${(props) => (props.$selected ? "#7F56D9" : "#667085")};
  transition: all 0.15s ease;
  pointer-events: ${(props) => (props.$compact ? "none" : "auto")};

  &:hover {
    border-color: ${(props) => (props.$compact ? "#D5D7DA" : "#7F56D9")};
    background: ${(props) => (props.$compact ? "#F9FAFB" : props.$selected ? "#EDE0FF" : "#F4EBFF")};
    color: ${(props) => (props.$compact ? "#667085" : "#7F56D9")};
  }
`;
